import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box} from "@mui/system";
import CircularProgress from "@mui/material/CircularProgress";
import {TextField, Tooltip, Typography} from "@mui/material";
import {Link} from "react-router-dom";

import {
    cancelMediaMarkersRender,
    collectMediaModuleMarkers,
    getCornerModuleRowConfigs,
    getMediaModuleWidthLabel,
    getMediaModuleGroups,
    isCornerSideBaseModule,
    MEDIA_IMAGE_DEFAULT_HEIGHT,
    MEDIA_IMAGE_DEFAULT_WIDTH,
    MEDIA_MODULE_MARKER_CORE_SIZE,
    MEDIA_MODULE_MARKER_RING_WIDTH,
    MEDIA_MODULE_MARKER_SIZE,
    catalogItemMediaStackMq,
    catalogItemModuleRowStackMq,
    catalogItemNarrowMq,
    normalizeModuleCompositionId,
    resolveCornerTypeForCornerModuleRow,
    resolveMediaModulePanelAnchorRowId,
    collectModuleCompositionRowIdentityKeys,
    resolveModuleCompositionHoverIdentityKeys,
    scheduleMediaMarkersRender,
    isCustomerModuleWidthLocked,
    getModuleCompositionDimension,
} from "../../utils/compositionMediaUtils";

const MODULE_SIZE_BLOCKED_TOOLTIP = "Размер менять нельзя";

const isModuleSizeBlocked = (row, lockWidthByBlockSize = false) => {
    if (lockWidthByBlockSize) {
        return isCustomerModuleWidthLocked(row);
    }
    return Boolean(row?.module?.blockSize);
};

const coordinateFieldSx = {
    flexShrink: 0,
    width: "74px",
    alignSelf: "stretch",
    [catalogItemMediaStackMq]: {
        width: "68px",
    },
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#F5F6FA",
        height: "100%",
        borderRadius: 0,
        padding: 0,
        "& fieldset": {border: "none"},
        "&:hover fieldset": {border: "none"},
        "&.Mui-focused fieldset": {border: "none"},
    },
    "& .MuiInputBase-input": {
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: 1.3,
        color: (theme) => theme.palette.text.main,
        fontFamily: (theme) => theme.typography.fontFamily,
        fontVariantNumeric: "tabular-nums",
        height: "100%",
        boxSizing: "border-box",
        py: 0,
        px: "6px",
        textAlign: "right",
        [catalogItemMediaStackMq]: {
            fontSize: "14px",
            px: "4px",
        },
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
        MozAppearance: "textfield",
    },
};

export const compositionMediaModulePanelSx = {
    width: "100%",
    maxWidth: "730px",
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    borderRadius: "16px",
    padding: "24px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    minHeight: "650px",
    maxHeight: "716px",
    overflowY: "auto",
    flex: "1 1 auto",
    [catalogItemMediaStackMq]: {
        padding: "16px",
        gap: "12px",
    },
};

export const mediaModuleRowSx = {
    backgroundColor: "#F5F6FA",
    borderRadius: "8px",
    width: "100%",
    maxWidth: "682px",
    minHeight: "40px",
    padding: "8px 12px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    gap: 0,
    [catalogItemMediaStackMq]: {
        minHeight: "36px",
        padding: "6px 8px",
    },
    [catalogItemNarrowMq]: {
        alignItems: "flex-start",
        minHeight: "min-content",
    },
};

const widthValueTextSx = {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.2,
    color: (theme) => theme.palette.text.main,
    fontFamily: (theme) => theme.typography.fontFamily,
    fontVariantNumeric: "tabular-nums",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
};

const mediaModulePrioritySx = {
    flexShrink: 0,
    width: "28px",
    alignSelf: "center",
    pr: "10px",
    boxSizing: "border-box",
    textAlign: "right",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.3,
    color: (theme) => theme.palette.primary.dark,
    fontFamily: (theme) => theme.typography.fontFamily,
    fontVariantNumeric: "tabular-nums",
    [catalogItemMediaStackMq]: {
        width: "26px",
        pr: "10px",
        fontSize: "14px",
    },
};

const formatMediaModulePriority = (row) => {
    const priority = row?.priority ?? row?.module?.priority;
    if (priority == null || priority === "") {
        return "";
    }
    return String(priority);
};

const mediaModuleNameSx = {
    flex: "1 1 auto",
    minWidth: 0,
    alignSelf: "center",
    pr: "2px",
    boxSizing: "border-box",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.2,
    color: (theme) => theme.palette.text.main,
    fontFamily: (theme) => theme.typography.fontFamily,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogItemNarrowMq]: {
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        alignSelf: "flex-start",
    },
};

export const mediaModuleSectionTitleSx = {
    fontSize: "22px",
    fontWeight: 400,
    lineHeight: 1.1,
    color: "#737478",
    [catalogItemMediaStackMq]: {
        fontSize: "18px",
    },
};

const widthLabelSx = {
    flex: "0 0 auto",
    flexShrink: 0,
    alignSelf: "center",
    width: "min-content",
    maxWidth: "min-content",
    backgroundColor: "#F5F6FA",
    pl: "2px",
    pr: "2px",
    whiteSpace: "nowrap",
    ...widthValueTextSx,
    fontSize: "14px",
    [catalogItemMediaStackMq]: {
        pl: "1px",
        pr: "1px",
    },
    [catalogItemNarrowMq]: {
        alignSelf: "flex-start",
    },
};

const widthControlsSx = {
    display: "flex",
    flexShrink: 0,
    alignItems: "stretch",
    marginLeft: "auto",
    [catalogItemNarrowMq]: {
        alignItems: "flex-start",
    },
};

const widthFieldSx = {
    flexShrink: 0,
    width: "52px",
    alignSelf: "stretch",
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#F5F6FA",
        height: "100%",
        borderRadius: 0,
        padding: 0,
        fontSize: "16px",
        fontFamily: (theme) => theme.typography.fontFamily,
        color: (theme) => theme.palette.text.main,
        [catalogItemMediaStackMq]: {
            fontSize: "14px",
        },
        "& fieldset": {border: "none"},
        "&:hover fieldset": {border: "none"},
        "&.Mui-focused fieldset": {border: "none"},
    },
    "& .MuiInputBase-input": {
        ...widthValueTextSx,
        height: "100%",
        boxSizing: "border-box",
        py: 0,
        pl: "2px",
        pr: "2px",
        textAlign: "right",
        [catalogItemMediaStackMq]: {
            pl: "1px",
            pr: "1px",
        },
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
        MozAppearance: "textfield",
    },
};

export const getModuleWidthInputId = (rowIdPrefix, rowId) =>
    `${rowIdPrefix}${rowId}-width`;

const catalogWidthRecalculatingOverlaySx = {
    position: "absolute",
    inset: 0,
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(245, 246, 250, 0.55)",
    pointerEvents: "auto",
};

/** Оверлей на превью кухни, пока PATCH ширины / пересчёт модулей на сервере. */
export const CatalogWidthRecalculatingOverlay = ({active}) => {
    if (!active) {
        return null;
    }
    return (
        <Box sx={catalogWidthRecalculatingOverlaySx} aria-busy="true" aria-live="polite">
            <CircularProgress disableShrink/>
        </Box>
    );
};

export const focusModuleWidthInput = (rowIdPrefix, rowId) => {
    document.getElementById(getModuleWidthInputId(rowIdPrefix, rowId))?.focus();
};

const MediaModuleMarker = React.memo(function MediaModuleMarker({
    moduleRow,
    isMarkerActive,
    readOnly,
    onMouseEnter,
    onMouseLeave,
    onClick,
}) {
    const x = Number(moduleRow.coordinateXPoint);
    const y = Number(moduleRow.coordinateYPoint);
    const left =
        !Number.isNaN(x) && MEDIA_IMAGE_DEFAULT_WIDTH > 0
            ? `${(x / MEDIA_IMAGE_DEFAULT_WIDTH) * 100}%`
            : `${moduleRow.coordinateXPoint}px`;
    const top =
        !Number.isNaN(y) && MEDIA_IMAGE_DEFAULT_HEIGHT > 0
            ? `${(y / MEDIA_IMAGE_DEFAULT_HEIGHT) * 100}%`
            : `${moduleRow.coordinateYPoint}px`;

    const priorityLabel = formatMediaModulePriority(moduleRow);
    const priorityText =
        priorityLabel !== "" ? priorityLabel : null;

    return (
        <Box
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            sx={{
                position: "absolute",
                left,
                top,
                transform: "translate(-50%, -50%)",
                width: `${MEDIA_MODULE_MARKER_SIZE}px`,
                height: `${MEDIA_MODULE_MARKER_SIZE}px`,
                cursor: onClick || !readOnly ? "pointer" : "default",
                zIndex: isMarkerActive ? 2 : 1,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    boxSizing: "border-box",
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    border: `${MEDIA_MODULE_MARKER_RING_WIDTH}px solid rgba(59, 139, 236, ${isMarkerActive ? 0.3 : 0.1})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        width: `${MEDIA_MODULE_MARKER_CORE_SIZE}px`,
                        height: `${MEDIA_MODULE_MARKER_CORE_SIZE}px`,
                        borderRadius: "50%",
                        backgroundColor: "#FFFFFF",
                        flexShrink: 0,
                    }}
                />
            </Box>
            {priorityText != null ? (
                <Box
                    sx={{
                        position: "absolute",
                        top: -4,
                        right: -6,
                        minWidth: priorityText.length > 1 ? "22px" : "18px",
                        height: "18px",
                        px: priorityText.length > 1 ? "4px" : 0,
                        borderRadius: priorityText.length > 1 ? "9px" : "50%",
                        backgroundColor: (theme) => theme.palette.primary.dark,
                        border: "1px solid #FFFFFF",
                        boxSizing: "border-box",
                        pointerEvents: "none",
                        display: "grid",
                        placeItems: "center",
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            color: "#FFFFFF",
                            fontSize: "12px",
                            fontWeight: 400,
                            lineHeight: 1,
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontVariantNumeric: "tabular-nums",
                            m: 0,
                            p: 0,
                        }}
                    >
                        {priorityText}
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
});

export const useCompositionMediaHover = () => {
    const hoverClearTimeoutRef = useRef(null);
    const hoverPinnedRef = useRef(false);
    const [hoveredModuleRowId, setHoveredModuleRowId] = useState(null);
    const [moduleRowHoverPinned, setModuleRowHoverPinned] = useState(false);

    const clearHoverTimeout = useCallback(() => {
        if (hoverClearTimeoutRef.current) {
            clearTimeout(hoverClearTimeoutRef.current);
            hoverClearTimeoutRef.current = null;
        }
    }, []);

    const setModuleRowHover = useCallback((rowId) => {
        if (hoverPinnedRef.current) {
            return;
        }
        clearHoverTimeout();
        setHoveredModuleRowId(rowId);
    }, [clearHoverTimeout]);

    const clearModuleRowHover = useCallback((rowId) => {
        if (hoverPinnedRef.current) {
            return;
        }
        clearHoverTimeout();
        hoverClearTimeoutRef.current = setTimeout(() => {
            setHoveredModuleRowId((prev) => (prev === rowId ? null : prev));
            hoverClearTimeoutRef.current = null;
        }, 40);
    }, [clearHoverTimeout]);

    /** Заморозить подсветку на шкафу (клик по сцене / маркеру). */
    const pinModuleRowHover = useCallback((rowId) => {
        clearHoverTimeout();
        hoverPinnedRef.current = true;
        setModuleRowHoverPinned(true);
        setHoveredModuleRowId(rowId);
    }, [clearHoverTimeout]);

    /**
     * Снять заморозку. nextRowId: id под курсором, null — сбросить подсветку,
     * undefined — оставить текущий hovered.
     */
    const unpinModuleRowHover = useCallback((nextRowId) => {
        clearHoverTimeout();
        hoverPinnedRef.current = false;
        setModuleRowHoverPinned(false);
        if (nextRowId !== undefined) {
            setHoveredModuleRowId(nextRowId);
        }
    }, [clearHoverTimeout]);

    useEffect(() => () => {
        if (hoverClearTimeoutRef.current) {
            clearTimeout(hoverClearTimeoutRef.current);
        }
    }, []);

    return {
        hoveredModuleRowId,
        moduleRowHoverPinned,
        setModuleRowHover,
        clearModuleRowHover,
        pinModuleRowHover,
        unpinModuleRowHover,
    };
};

export const useCompositionMediaMarkers = ({
    moduleCompositions,
    enabled,
    isFirstPictureSelected,
    mainImageUrl,
    mainImageRef,
}) => {
    const markersRenderTaskRef = useRef(null);
    const [markersReady, setMarkersReady] = useState(false);

    const resetMarkersRender = useCallback(() => {
        if (markersRenderTaskRef.current != null) {
            cancelMediaMarkersRender(markersRenderTaskRef.current);
            markersRenderTaskRef.current = null;
        }
        setMarkersReady(false);
    }, []);

    const queueMarkersRender = useCallback(() => {
        if (markersRenderTaskRef.current != null) {
            cancelMediaMarkersRender(markersRenderTaskRef.current);
        }
        markersRenderTaskRef.current = scheduleMediaMarkersRender(() => {
            markersRenderTaskRef.current = null;
            setMarkersReady(true);
        });
    }, []);

    const handleMainImageLoad = useCallback(() => {
        if (!isFirstPictureSelected) {
            return;
        }
        queueMarkersRender();
    }, [isFirstPictureSelected, queueMarkersRender]);

    useEffect(() => {
        if (!enabled) {
            resetMarkersRender();
            return undefined;
        }
        resetMarkersRender();
        if (!isFirstPictureSelected || !mainImageUrl) {
            return undefined;
        }
        const img = mainImageRef.current;
        if (img?.complete) {
            queueMarkersRender();
        }
        return () => {
            if (markersRenderTaskRef.current != null) {
                cancelMediaMarkersRender(markersRenderTaskRef.current);
                markersRenderTaskRef.current = null;
            }
        };
    }, [
        enabled,
        mainImageUrl,
        isFirstPictureSelected,
        queueMarkersRender,
        resetMarkersRender,
        mainImageRef,
    ]);

    const markers = useMemo(() => {
        if (!enabled || !isFirstPictureSelected || !markersReady) {
            return [];
        }
        return collectMediaModuleMarkers(moduleCompositions);
    }, [moduleCompositions, enabled, isFirstPictureSelected, markersReady]);

    return {
        markers,
        handleMainImageLoad,
    };
};

const mediaModuleRowSpacerSx = {
    minHeight: "40px",
    flexShrink: 0,
    [catalogItemMediaStackMq]: {
        minHeight: "36px",
    },
};

export const mediaModuleSectionLayoutSx = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    mt: "24px",
    alignItems: "stretch",
    width: "100%",
    [catalogItemMediaStackMq]: {
        gap: "6px",
        mt: "16px",
    },
};

export const mediaModuleSectionRowSx = {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    alignItems: "flex-start",
    width: "100%",
    minWidth: 0,
    [catalogItemMediaStackMq]: {
        gap: "6px",
    },
    [catalogItemModuleRowStackMq]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: "8px",
    },
};

export const mediaModuleSectionColumnsSx = {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    [catalogItemMediaStackMq]: {
        gap: "6px",
    },
    [catalogItemModuleRowStackMq]: {
        flex: "1 1 auto",
        width: "100%",
        maxWidth: "100%",
    },
};

const CompositionMediaModuleRow = ({
    row,
    reactKey,
    rowIdPrefix,
    isHighlighted,
    onMouseEnter,
    onMouseLeave,
    editableWidth = false,
    onWidthChange,
    onWidthFocus,
    onWidthBlur,
    onWidthKeyDown,
    widthInputsDisabled = false,
    onFocusWidth,
    widthLabel = "Ширина",
    widthInputTooltip,
    sizeField = "width",
    sizeLabel,
    showModuleName = true,
    showCoordinates = false,
    coordinateInputsDisabled = false,
    coordinateMaxX = MEDIA_IMAGE_DEFAULT_WIDTH,
    coordinateMaxY = MEDIA_IMAGE_DEFAULT_HEIGHT,
    onCoordinateChange,
    onCoordinateFocus,
    onCoordinateBlur,
    linkModuleName = false,
}) => {
    const fieldLabel =
        sizeLabel ?? (sizeField === "depth" ? "Глубина" : widthLabel);
    const fieldValue = getModuleCompositionDimension(row, sizeField) ?? "";
    const inputId =
        sizeField === "width"
            ? getModuleWidthInputId(rowIdPrefix, row.id)
            : `${rowIdPrefix}${row.id}-${sizeField}`;

    const moduleNameContent =
        linkModuleName && row.module?.id ? (
            <Box
                component={Link}
                to={`/organization/module/${row.module.id}`}
                onClick={(event) => event.stopPropagation()}
                sx={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "inherit",
                    textDecoration: "none",
                    fontWeight: "inherit",
                    fontSize: "inherit",
                    lineHeight: "inherit",
                }}
            >
                {row.name}
            </Box>
        ) : (
            row.name
        );

    if (showCoordinates) {
        const priorityLabel = formatMediaModulePriority(row);
        return (
            <Box
                key={reactKey ?? row.id}
                id={`${rowIdPrefix}${row.id}`}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                sx={{
                    ...mediaModuleRowSx,
                    ...(isHighlighted
                        ? {
                            boxShadow: "inset 0 0 0 2px #3B8BEC",
                        }
                        : {}),
                }}
            >
                <Typography
                    component="span"
                    aria-hidden={!priorityLabel}
                    sx={mediaModulePrioritySx}
                >
                    {priorityLabel}
                </Typography>
                <Typography title={row.name} sx={mediaModuleNameSx}>
                    {moduleNameContent}
                </Typography>
                <TextField
                    type="number"
                    size="small"
                    value={row.coordinateXPoint ?? ""}
                    disabled={coordinateInputsDisabled}
                    onFocus={() => onCoordinateFocus?.(row)}
                    onChange={(event) =>
                        onCoordinateChange?.(row.id, "coordinateXPoint", event.target.value)
                    }
                    onBlur={() => onCoordinateBlur?.(row.id)}
                    onClick={(event) => event.stopPropagation()}
                    sx={coordinateFieldSx}
                    inputProps={{
                        "aria-label": "Координата X",
                        min: 0,
                        max: coordinateMaxX,
                    }}
                />
                <TextField
                    type="number"
                    size="small"
                    value={row.coordinateYPoint ?? ""}
                    disabled={coordinateInputsDisabled}
                    onFocus={() => onCoordinateFocus?.(row)}
                    onChange={(event) =>
                        onCoordinateChange?.(row.id, "coordinateYPoint", event.target.value)
                    }
                    onBlur={() => onCoordinateBlur?.(row.id)}
                    onClick={(event) => event.stopPropagation()}
                    sx={coordinateFieldSx}
                    inputProps={{
                        "aria-label": "Координата Y",
                        min: 0,
                        max: coordinateMaxY,
                    }}
                />
            </Box>
        );
    }

    const sizeInput = (
            <TextField
                type="number"
                size="small"
                value={fieldValue}
                disabled={widthInputsDisabled || !editableWidth}
                onFocus={
                    editableWidth && onWidthFocus
                        ? () => onWidthFocus(row)
                        : undefined
                }
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onBlur={
                    editableWidth && onWidthBlur
                        ? () => onWidthBlur(row.id)
                        : undefined
                }
                onChange={
                    editableWidth && onWidthChange
                        ? (event) => onWidthChange(row.id, event.target.value)
                        : undefined
                }
                onKeyDown={
                    editableWidth && onWidthKeyDown
                        ? (event) => onWidthKeyDown(row.id, event)
                        : undefined
                }
                onClick={(event) => event.stopPropagation()}
                sx={widthFieldSx}
                inputProps={{
                    id: inputId,
                    "aria-label": fieldLabel,
                }}
            />
    );

    return (
    <Box
        key={reactKey ?? `${row.id}-${sizeField}`}
        id={sizeField === "width" ? `${rowIdPrefix}${row.id}` : undefined}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={(event) => {
            if (!editableWidth || event.target.closest("input, textarea, button")) {
                return;
            }
            onFocusWidth?.(row.id);
        }}
        sx={{
            ...mediaModuleRowSx,
            ...(!showModuleName
                ? {
                    width: "fit-content",
                    maxWidth: "none",
                    alignSelf: "flex-end",
                    marginLeft: "auto",
                }
                : {}),
            ...(editableWidth && !widthInputsDisabled ? {cursor: "text"} : {}),
            ...(isHighlighted
                ? {
                    boxShadow: "inset 0 0 0 2px #3B8BEC",
                }
                : {}),
        }}
    >
        {showModuleName ? (
            <Typography title={row.name} sx={mediaModuleNameSx}>
                {moduleNameContent}
            </Typography>
        ) : null}
        <Box sx={widthControlsSx}>
            <Typography component="span" sx={widthLabelSx}>
                {fieldLabel}
            </Typography>
            <Box
                component="span"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                sx={{display: "inline-flex", alignSelf: "stretch"}}
            >
                {widthInputTooltip ? (
                    <Tooltip title={widthInputTooltip} arrow>
                        <Box component="span" sx={{display: "inline-flex", alignSelf: "stretch"}}>
                            {sizeInput}
                        </Box>
                    </Tooltip>
                ) : (
                    sizeInput
                )}
            </Box>
        </Box>
    </Box>
    );
};

const CompositionMediaModuleSection = ({
    title,
    primaryRows,
    dependentsByParentId,
    rowIdPrefix,
    hoveredModuleRowId,
    setModuleRowHover,
    clearModuleRowHover,
    editableWidth,
    primaryEditableWidth,
    dependentEditableWidth,
    lockWidthByBlockSize = false,
    onWidthChange,
    onWidthFocus,
    onWidthBlur,
    onWidthKeyDown,
    widthInputsDisabled,
    onFocusWidth,
    widthLabel = "Ширина",
    widthInputTooltip,
    dependentWidthInputTooltip,
    primaryShowDepthRow = false,
    primaryDepthLabel = "Глубина",
    primarySizeField = "width",
    secondarySizeField = "depth",
    getPrimaryRowConfigs,
    prioritizeCornerDependent = false,
    getDependentRowWidthLabel,
    showCoordinates = false,
    coordinateInputsDisabled = false,
    coordinateMaxX = MEDIA_IMAGE_DEFAULT_WIDTH,
    coordinateMaxY = MEDIA_IMAGE_DEFAULT_HEIGHT,
    onCoordinateChange,
    onCoordinateFocus,
    onCoordinateBlur,
    linkModuleName = false,
}) => {
    const pageAllowsPrimaryEdit = primaryEditableWidth ?? editableWidth;
    const pageAllowsDependentEdit = dependentEditableWidth ?? editableWidth;
    const isEmpty = primaryRows.length === 0;
    const useCoordinates = Boolean(showCoordinates);

    const getSizeRowTooltip = (row, sizeField, fallbackTooltip) => {
        if (
            lockWidthByBlockSize
            && sizeField === "width"
            && isModuleSizeBlocked(row, lockWidthByBlockSize)
        ) {
            return MODULE_SIZE_BLOCKED_TOOLTIP;
        }
        return fallbackTooltip;
    };

    const getResolvedPrimaryRowConfigs = (row) =>
        getPrimaryRowConfigs?.(row) ?? ({
            widthLabel,
            primaryDepthLabel,
            primarySizeField,
            secondarySizeField,
        });

    const renderCoordinateRow = (row, reactKey) => (
        <CompositionMediaModuleRow
            key={reactKey ?? row.id}
            row={row}
            reactKey={reactKey ?? row.id}
            rowIdPrefix={rowIdPrefix}
            isHighlighted={normalizeModuleCompositionId(hoveredModuleRowId) === normalizeModuleCompositionId(row.id)}
            onMouseEnter={() => setModuleRowHover(row.id)}
            onMouseLeave={() => clearModuleRowHover(row.id)}
            showCoordinates
            coordinateInputsDisabled={coordinateInputsDisabled}
            coordinateMaxX={coordinateMaxX}
            coordinateMaxY={coordinateMaxY}
            onCoordinateChange={onCoordinateChange}
            onCoordinateFocus={onCoordinateFocus}
            onCoordinateBlur={onCoordinateBlur}
            linkModuleName={linkModuleName}
        />
    );

    const renderSizeRow = (row, {
        reactKey,
        sizeField = "width",
        sizeLabel,
        widthLabel: rowWidthLabel,
        showModuleName = true,
        pageAllowsEdit,
        fallbackTooltip,
    }) => {
        const canEdit = Boolean(pageAllowsEdit)
            && sizeField === "width"
            && (!lockWidthByBlockSize || !isModuleSizeBlocked(row, lockWidthByBlockSize));
        return (
            <CompositionMediaModuleRow
                key={reactKey}
                row={row}
                rowIdPrefix={rowIdPrefix}
                isHighlighted={normalizeModuleCompositionId(hoveredModuleRowId) === normalizeModuleCompositionId(row.id)}
                onMouseEnter={() => setModuleRowHover(row.id)}
                onMouseLeave={() => clearModuleRowHover(row.id)}
                editableWidth={canEdit}
                onWidthChange={canEdit ? onWidthChange : undefined}
                onWidthFocus={canEdit ? onWidthFocus : undefined}
                onWidthBlur={canEdit ? onWidthBlur : undefined}
                onWidthKeyDown={canEdit ? onWidthKeyDown : undefined}
                widthInputsDisabled={widthInputsDisabled}
                onFocusWidth={canEdit ? onFocusWidth : undefined}
                widthLabel={rowWidthLabel}
                widthInputTooltip={getSizeRowTooltip(row, sizeField, fallbackTooltip)}
                sizeField={sizeField}
                sizeLabel={sizeLabel}
                showModuleName={showModuleName}
            />
        );
    };

    const renderPrimaryWidthRow = (row) => {
        if (useCoordinates) {
            return renderCoordinateRow(row);
        }
        const rowConfigs = getResolvedPrimaryRowConfigs(row);
        return renderSizeRow(row, {
            sizeField: rowConfigs.primarySizeField,
            sizeLabel: rowConfigs.widthLabel,
            widthLabel: rowConfigs.widthLabel,
            pageAllowsEdit: pageAllowsPrimaryEdit,
            fallbackTooltip: widthInputTooltip,
        });
    };

    const renderPrimaryDepthRow = (row) => {
        const rowConfigs = getResolvedPrimaryRowConfigs(row);
        return renderSizeRow(row, {
            sizeField: rowConfigs.secondarySizeField,
            sizeLabel: rowConfigs.primaryDepthLabel,
            widthLabel: rowConfigs.primaryDepthLabel,
            showModuleName: false,
            pageAllowsEdit: pageAllowsPrimaryEdit,
            fallbackTooltip: widthInputTooltip,
        });
    };

    const renderPrimaryLeftCell = (row) => {
        const shouldShowSecondRow =
            !useCoordinates &&
            primaryShowDepthRow &&
            isCornerSideBaseModule(row?.sideBaseModule);
        if (!shouldShowSecondRow) {
            return renderPrimaryWidthRow(row);
        }
        return (
            <Box
                key={row.id}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    width: "100%",
                    [catalogItemMediaStackMq]: {
                        gap: "6px",
                    },
                }}
            >
                {renderPrimaryWidthRow(row)}
                {renderPrimaryDepthRow(row)}
            </Box>
        );
    };

    const renderDependentSlot = (primaryRow) => {
        const deps =
            dependentsByParentId?.get(
                normalizeModuleCompositionId(primaryRow.id),
            ) ?? [];
        if (deps.length === 0) {
            return null;
        }
        const isCornerDependentRow = (row) =>
            row?.sideBaseModule === "CORNER_LEFT" ||
            row?.sideBaseModule === "CORNER__LEFT" ||
            row?.sideBaseModule === "CORNER_RIGHT" ||
            row?.sideBaseModule === "CORNER__RIGHT";
        let sortedDeps = deps;
        if (!useCoordinates && prioritizeCornerDependent && deps.length > 1) {
            const cornerDepIndex = deps.findIndex((dep) =>
                isCornerDependentRow(dep),
            );
            if (cornerDepIndex > 0) {
                const cornerDep = deps[cornerDepIndex];
                sortedDeps = [
                    cornerDep,
                    ...deps.slice(0, cornerDepIndex),
                    ...deps.slice(cornerDepIndex + 1),
                ];
            }
        }
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    width: "100%",
                    minWidth: 0,
                    [catalogItemMediaStackMq]: {
                        gap: "6px",
                    },
                }}
            >
                {sortedDeps.map((dep, idx) => {
                    const depKey = `dep-${primaryRow.id}-${dep.id ?? idx}`;
                    if (useCoordinates) {
                        return renderCoordinateRow(dep, depKey);
                    }
                    const isCornerDependent =
                        prioritizeCornerDependent &&
                        deps.length > 1 &&
                        isCornerDependentRow(dep);

                    const dependentFallbackTooltip =
                        dependentWidthInputTooltip ?? widthInputTooltip;
                    if (isCornerDependent) {
                        const depCornerType = resolveCornerTypeForCornerModuleRow(dep);
                        const [depPrimary, depSecondary] = getCornerModuleRowConfigs(depCornerType);
                        return (
                            <Box
                                key={depKey}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                    width: "100%",
                                    [catalogItemMediaStackMq]: {
                                        gap: "6px",
                                    },
                                }}
                            >
                                {renderSizeRow(dep, {
                                    sizeField: depPrimary.sizeField,
                                    sizeLabel: depPrimary.label,
                                    widthLabel: depPrimary.label,
                                    pageAllowsEdit: pageAllowsDependentEdit,
                                    fallbackTooltip: dependentFallbackTooltip,
                                })}
                                {renderSizeRow(dep, {
                                    sizeField: depSecondary.sizeField,
                                    sizeLabel: depSecondary.label,
                                    widthLabel: depSecondary.label,
                                    showModuleName: false,
                                    pageAllowsEdit: pageAllowsDependentEdit,
                                    fallbackTooltip: dependentFallbackTooltip,
                                })}
                            </Box>
                        );
                    }

                    return renderSizeRow(dep, {
                        reactKey: depKey,
                        widthLabel: getDependentRowWidthLabel?.(dep) ?? widthLabel,
                        sizeLabel: getDependentRowWidthLabel?.(dep) ?? widthLabel,
                        pageAllowsEdit: pageAllowsDependentEdit,
                        fallbackTooltip: dependentFallbackTooltip,
                    });
                })}
            </Box>
        );
    };

    return (
        <Box sx={{width: "100%"}}>
            <Typography sx={mediaModuleSectionTitleSx}>
                {title}
            </Typography>
            {isEmpty ? (
                <Typography
                    sx={{
                        mt: "24px",
                        fontSize: "14px",
                        color: "text.secondary",
                        [catalogItemMediaStackMq]: {
                            mt: "16px",
                            fontSize: "13px",
                        },
                    }}
                >
                    Нет модулей в этой группе
                </Typography>
            ) : (
                <Box sx={mediaModuleSectionLayoutSx}>
                    {primaryRows.map((primaryRow) => {
                        const slot = renderDependentSlot(primaryRow);
                        const shouldRenderDepthSpacer =
                            !useCoordinates &&
                            primaryShowDepthRow &&
                            !prioritizeCornerDependent;
                        return (
                            <Box
                                key={primaryRow.id}
                                sx={mediaModuleSectionRowSx}
                            >
                                <Box sx={mediaModuleSectionColumnsSx}>
                                    {slot}
                                    {shouldRenderDepthSpacer ? (
                                        slot ? (
                                            <Box sx={mediaModuleRowSpacerSx} />
                                        ) : (
                                            <>
                                                <Box sx={mediaModuleRowSpacerSx} />
                                                <Box sx={mediaModuleRowSpacerSx} />
                                            </>
                                        )
                                    ) : null}
                                </Box>
                                <Box sx={mediaModuleSectionColumnsSx}>
                                    {renderPrimaryLeftCell(primaryRow)}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export const CompositionMediaModulePanel = ({
    moduleCompositions,
    cornerType,
    rowIdPrefix = "media-module-row-",
    hoveredModuleRowId,
    setModuleRowHover,
    clearModuleRowHover,
    editableWidth = false,
    onWidthChange,
    onWidthFocus,
    onWidthBlur,
    onWidthKeyDown,
    widthInputsDisabled = false,
    widthInputTooltip,
    lockWidthByBlockSize = false,
    includeTopModules = false,
    showCoordinates = false,
    coordinateInputsDisabled = false,
    coordinateMaxX = MEDIA_IMAGE_DEFAULT_WIDTH,
    coordinateMaxY = MEDIA_IMAGE_DEFAULT_HEIGHT,
    onCoordinateChange,
    onCoordinateFocus,
    onCoordinateBlur,
    linkModuleName = false,
    sx,
}) => {
    const mediaModuleGroups = useMemo(
        () => getMediaModuleGroups(moduleCompositions, {includeTop: includeTopModules}),
        [moduleCompositions, includeTopModules],
    );

    const leftWidthLabel = getMediaModuleWidthLabel(cornerType, "left");
    const rightWidthLabel = getMediaModuleWidthLabel(cornerType, "right");
    const hasCornerModules = mediaModuleGroups.corner.primary.length > 0;
    const useCoordinates = Boolean(showCoordinates);
    const lockWidthByLegacyType = !lockWidthByBlockSize;

    const coordinateSectionProps = useCoordinates
        ? {
            showCoordinates: true,
            coordinateInputsDisabled,
            coordinateMaxX,
            coordinateMaxY,
            onCoordinateChange,
            onCoordinateFocus,
            onCoordinateBlur,
            linkModuleName,
        }
        : {};

    const handleFocusWidth = useCallback(
        (rowId) => {
            setModuleRowHover(rowId);
            focusModuleWidthInput(rowIdPrefix, rowId);
        },
        [rowIdPrefix, setModuleRowHover],
    );

    return (
        <Box
            sx={{
                ...compositionMediaModulePanelSx,
                ...sx,
            }}
        >
            <CompositionMediaModuleSection
                title="Левые модули"
                primaryRows={mediaModuleGroups.left.primary}
                dependentsByParentId={mediaModuleGroups.left.dependentsByParentId}
                rowIdPrefix={rowIdPrefix}
                hoveredModuleRowId={hoveredModuleRowId}
                setModuleRowHover={setModuleRowHover}
                clearModuleRowHover={clearModuleRowHover}
                editableWidth={editableWidth}
                dependentEditableWidth={lockWidthByLegacyType ? false : undefined}
                dependentWidthInputTooltip={lockWidthByLegacyType ? "Эти размеры менять нельзя" : undefined}
                lockWidthByBlockSize={lockWidthByBlockSize}
                widthInputTooltip={widthInputTooltip}
                widthLabel={leftWidthLabel}
                onWidthChange={onWidthChange}
                onWidthFocus={onWidthFocus}
                onWidthBlur={onWidthBlur}
                onWidthKeyDown={onWidthKeyDown}
                widthInputsDisabled={widthInputsDisabled}
                onFocusWidth={handleFocusWidth}
                {...coordinateSectionProps}
            />
            {hasCornerModules && (
                <CompositionMediaModuleSection
                    title="Угловые модули"
                    primaryRows={mediaModuleGroups.corner.primary}
                    dependentsByParentId={mediaModuleGroups.corner.dependentsByParentId}
                    rowIdPrefix={rowIdPrefix}
                    hoveredModuleRowId={hoveredModuleRowId}
                    setModuleRowHover={setModuleRowHover}
                    clearModuleRowHover={clearModuleRowHover}
                    editableWidth={editableWidth}
                    primaryEditableWidth={lockWidthByLegacyType ? false : undefined}
                    dependentEditableWidth={lockWidthByLegacyType ? false : undefined}
                    lockWidthByBlockSize={lockWidthByBlockSize}
                    primaryShowDepthRow={!useCoordinates}
                    getPrimaryRowConfigs={useCoordinates ? undefined : ((row) => {
                        const rowCornerType = resolveCornerTypeForCornerModuleRow(row, cornerType);
                        const [primary, secondary] = getCornerModuleRowConfigs(rowCornerType);
                        return {
                            widthLabel: primary.label,
                            primaryDepthLabel: secondary.label,
                            primarySizeField: primary.sizeField,
                            secondarySizeField: secondary.sizeField,
                        };
                    })}
                    getDependentRowWidthLabel={useCoordinates ? undefined : ((row) => {
                        if (row?.sideBaseModule === "LEFT") {
                            return getMediaModuleWidthLabel(cornerType, "left");
                        }
                        if (row?.sideBaseModule === "RIGHT") {
                            return getMediaModuleWidthLabel(cornerType, "right");
                        }
                        return "Ширина";
                    })}
                    prioritizeCornerDependent={!useCoordinates}
                    widthInputTooltip={
                        lockWidthByLegacyType
                            ? "Размеры угловых модулей менять нельзя"
                            : widthInputTooltip
                    }
                    onWidthChange={onWidthChange}
                    onWidthFocus={onWidthFocus}
                    onWidthBlur={onWidthBlur}
                    onWidthKeyDown={onWidthKeyDown}
                    widthInputsDisabled={widthInputsDisabled}
                    onFocusWidth={handleFocusWidth}
                    {...coordinateSectionProps}
                />
            )}
            <CompositionMediaModuleSection
                title="Правые модули"
                primaryRows={mediaModuleGroups.right.primary}
                dependentsByParentId={mediaModuleGroups.right.dependentsByParentId}
                rowIdPrefix={rowIdPrefix}
                hoveredModuleRowId={hoveredModuleRowId}
                setModuleRowHover={setModuleRowHover}
                clearModuleRowHover={clearModuleRowHover}
                editableWidth={editableWidth}
                dependentEditableWidth={lockWidthByLegacyType ? false : undefined}
                dependentWidthInputTooltip={lockWidthByLegacyType ? "Эти размеры менять нельзя" : undefined}
                lockWidthByBlockSize={lockWidthByBlockSize}
                widthInputTooltip={widthInputTooltip}
                widthLabel={rightWidthLabel}
                onWidthChange={onWidthChange}
                onWidthFocus={onWidthFocus}
                onWidthBlur={onWidthBlur}
                onWidthKeyDown={onWidthKeyDown}
                widthInputsDisabled={widthInputsDisabled}
                onFocusWidth={handleFocusWidth}
                {...coordinateSectionProps}
            />
        </Box>
    );
};

export const CompositionMediaImageMarkers = ({
    markers,
    readOnly = true,
    rowIdPrefix = "media-module-row-",
    hoveredModuleRowId,
    moduleCompositions = null,
    setModuleRowHover,
    clearModuleRowHover,
    focusWidthOnClick = false,
}) => (
    <>
        {markers.map((moduleRow) => {
            const panelRowId = resolveMediaModulePanelAnchorRowId(
                moduleRow,
                moduleCompositions,
            );
            const scrollToPanelRow = () => {
                document
                    .getElementById(`${rowIdPrefix}${panelRowId}`)
                    ?.scrollIntoView({block: "nearest", behavior: "smooth"});
            };
            const hoverHighlightRowIds = resolveModuleCompositionHoverIdentityKeys(
                hoveredModuleRowId,
                moduleCompositions,
            );
            const isMarkerActive = [...collectModuleCompositionRowIdentityKeys(moduleRow)]
                .some((key) => hoverHighlightRowIds.has(key))
                || [...collectModuleCompositionRowIdentityKeys({id: panelRowId})]
                    .some((key) => hoverHighlightRowIds.has(key));

            return (
            <MediaModuleMarker
                key={moduleRow.id}
                moduleRow={moduleRow}
                readOnly={readOnly && !focusWidthOnClick}
                isMarkerActive={isMarkerActive}
                onMouseEnter={() => {
                    setModuleRowHover(panelRowId);
                    scrollToPanelRow();
                }}
                onMouseLeave={() => clearModuleRowHover(panelRowId)}
                onClick={
                    focusWidthOnClick
                        ? () => {
                            setModuleRowHover(panelRowId);
                            scrollToPanelRow();
                            focusModuleWidthInput(rowIdPrefix, panelRowId);
                        }
                        : undefined
                }
            />
            );
        })}
    </>
);
